import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

import { PLAN_COLORS } from '../constants';
import type { AddressEvent, GeneratedPlan, PlanConfig } from '../types';
import {
  capAtFromSchedule,
  fleetCapFromSchedule,
  totalEmailDemand,
} from '../utils/nurturingPlanEngine';
import { Pill } from './Pill';

interface EmailAddressesTabProps {
  addrEvents: AddressEvent[];
  starts: number[];
  config: PlanConfig;
  plan?: GeneratedPlan | null;
}

const SectionHeader = ({ children, mt }: { children: ReactNode; mt?: number }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt }}>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: 'text.secondary',
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
  </Box>
);

const Stat = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{value}</Typography>
  </Box>
);

const eventCardSx = (color: string, bg: string) => ({
  borderRadius: 1.5,
  p: 1.75,
  mb: 1.25,
  borderLeft: `4px solid ${color}`,
  bgcolor: bg,
});

export const EmailAddressesTab = ({
  addrEvents,
  starts,
  config: c,
  plan,
}: EmailAddressesTabProps) => {
  const warmup = c.warmupSchedule;
  const launchEvent = addrEvents.find((e) => e.isLaunch);
  const eagerLaunch = launchEvent?.isEager;
  const triggerEvents = addrEvents.filter((e) => !e.isLaunch);

  const d1dem = totalEmailDemand(1, c.total, c.monthly, c.ramp, c.newStart, c.freqS);
  const steadyDem = plan?.steadyDemand ?? plan?.summary.steadyDemand ?? Math.ceil((c.total + c.monthly * 12) / c.freqS);
  const peakDem = plan?.peakDemand ?? plan?.summary.peakDemand ?? d1dem;

  let futureDay: number | null = null;
  let futureDem: number | null = null;
  if (!eagerLaunch && triggerEvents.length === 0) {
    for (let d = 1; d <= 1460; d += 1) {
      const dem = totalEmailDemand(d, c.total, c.monthly, c.ramp, c.newStart, c.freqS);
      const capV = fleetCapFromSchedule(d, starts, warmup);
      if (dem > capV * 0.8) {
        futureDay = d;
        futureDem = dem;
        break;
      }
    }
  }

  const milestones = [
    { label: 'Day 1', day: 1 },
    { label: `Day ${Math.round(c.ramp / 2)}`, day: Math.round(c.ramp / 2) },
    { label: `Day ${c.newStart} (new leads start)`, day: c.newStart },
    { label: `Day ${c.ramp} (ramp end)`, day: c.ramp },
    { label: 'Month 6 (day 180)', day: 180 },
    { label: 'Month 12 (day 365)', day: 365 },
    { label: 'Year 2 (day 730)', day: 730 },
  ]
    .filter((m, i, arr) => arr.findIndex((x) => x.day === m.day) === i)
    .sort((a, b) => a.day - b.day);

  return (
    <Box>
      <SectionHeader>Address provisioning schedule</SectionHeader>

      {eagerLaunch && launchEvent ? (
        <Box sx={eventCardSx(PLAN_COLORS.orange, PLAN_COLORS.orangeBg)}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: PLAN_COLORS.orange, mb: 0.5 }}>
            Day 1 — All {launchEvent.totalAddresses ?? starts.length} addresses provisioned (eager)
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
            All sending addresses start on Day 1 so the full fleet is warmed by day {c.ramp}. Peak
            demand ~{peakDem.toLocaleString()}/day vs single-address day-{c.ramp} capacity of{' '}
            {capAtFromSchedule(c.ramp, warmup).toLocaleString()}/day.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2.5, mt: 1, flexWrap: 'wrap' }}>
            <Stat label="Addresses" value={String(starts.length)} />
            <Stat label="Day 1 fleet cap" value={`${launchEvent.capAfter.toLocaleString()}/day`} />
            <Stat label="Peak demand" value={`${peakDem}/day`} />
          </Box>
        </Box>
      ) : (
        <Box sx={eventCardSx(PLAN_COLORS.green, PLAN_COLORS.greenBg)}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: PLAN_COLORS.green, mb: 0.5 }}>
            Day 1 — Address #1 provisioned (launch)
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
            Warm-up starts at {capAtFromSchedule(1, warmup)} emails/day, ramping to {c.cap.toLocaleString()}/day
            over {c.ramp} days. Past leads load immediately; new unsold leads start day {c.newStart}.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2.5, mt: 1, flexWrap: 'wrap' }}>
            <Stat label="Initial capacity" value={`${capAtFromSchedule(1, warmup)}/day`} />
            <Stat label="Full capacity by" value={`Day ${c.ramp}`} />
            <Stat label="Demand on day 1" value={`${d1dem}/day`} />
            <Stat label="New leads start" value={`Day ${c.newStart}`} />
          </Box>
        </Box>
      )}

      {!eagerLaunch && triggerEvents.length === 0 && futureDay && (
        <Box sx={eventCardSx(PLAN_COLORS.newBatch, PLAN_COLORS.amberBg)}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: PLAN_COLORS.newBatch, mb: 0.5 }}>
            ~Day {futureDay} — Address #2 projected
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
            Demand reaches 80% of capacity. Provision address #2 by day {Math.max(1, futureDay - 30)}.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2.5, mt: 1, flexWrap: 'wrap' }}>
            <Stat label="Projected demand" value={`${futureDem}/day`} />
            <Stat label="Fleet capacity" value={`${fleetCapFromSchedule(futureDay, starts, warmup)}/day`} />
            <Stat label="Provision by" value={`Day ${Math.max(1, futureDay - 30)}`} />
          </Box>
        </Box>
      )}

      {!eagerLaunch && triggerEvents.length === 0 && !futureDay && (
        <Box sx={eventCardSx(PLAN_COLORS.green, '#f7f9fc')}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: PLAN_COLORS.green, mb: 0.5 }}>
            Address #1 sufficient through Year 2
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
            Single address handles all projected demand with current configuration.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2.5, mt: 1, flexWrap: 'wrap' }}>
            <Stat label="Steady-state demand" value={`${steadyDem}/day`} />
            <Stat label="Address capacity" value={`${c.cap.toLocaleString()}/day`} />
            <Stat
              label="Utilisation"
              value={`${Math.round((steadyDem / Math.max(c.cap, 1)) * 100)}%`}
            />
          </Box>
        </Box>
      )}

      {triggerEvents.map((e) => (
        <Box key={String(e.idx)} sx={eventCardSx(PLAN_COLORS.orange, PLAN_COLORS.orangeBg)}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: PLAN_COLORS.orange, mb: 0.5 }}>
            ~Day {e.day} — Address #{e.idx} needed · provision by {e.provDay}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
            Demand hits 80% of fleet capacity. New address follows the warm-up schedule (
            {capAtFromSchedule(1, warmup)}/day → {c.cap.toLocaleString()}/day).
          </Typography>
          <Box sx={{ display: 'flex', gap: 2.5, mt: 1, flexWrap: 'wrap' }}>
            <Stat label="Demand at trigger" value={`${e.needed.toLocaleString()}/day`} />
            <Stat label="Fleet cap before" value={`${e.capBefore.toLocaleString()}/day`} />
            <Stat label="Fleet cap after" value={`${e.capAfter.toLocaleString()}/day`} />
            <Stat label="Provision by" value={e.provDay} />
          </Box>
        </Box>
      ))}

      <SectionHeader mt={2.5}>Capacity vs demand over time</SectionHeader>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1.25 }}>
        Grey = fleet capacity · coloured fill = demand (blue = under cap, red = over cap)
      </Typography>

      {milestones.map((m) => {
        const dem = totalEmailDemand(m.day, c.total, c.monthly, c.ramp, c.newStart, c.freqS);
        const capV = fleetCapFromSchedule(m.day, starts, warmup);
        const util = capV > 0 ? Math.round((dem / capV) * 100) : 0;
        const demPct = Math.min(100, capV > 0 ? Math.round((dem / Math.max(capV, dem)) * 100) : 0);
        const isOver = dem > capV;
        const utilColor =
          util >= 100 ? PLAN_COLORS.red : util >= 70 ? PLAN_COLORS.newBatch : PLAN_COLORS.green;
        const addrCount = starts.filter((s) => s <= m.day).length;
        const isNewStartDay = m.day === c.newStart;
        return (
          <Box
            key={m.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              mb: 0.875,
              fontSize: 12,
              ...(isNewStartDay
                ? { bgcolor: PLAN_COLORS.bothBg, borderRadius: 0.75, px: 0.5, mx: -0.5 }
                : {}),
            }}
          >
            <Box
              sx={{
                minWidth: 170,
                color: 'text.secondary',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {m.label}
              {isNewStartDay && (
                <Pill color={PLAN_COLORS.both} bg={PLAN_COLORS.bothBg} sx={{ fontSize: 9 }}>
                  new leads
                </Pill>
              )}
            </Box>
            <Box
              sx={{
                flex: 1,
                height: 12,
                bgcolor: 'grey.300',
                borderRadius: 6,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  borderRadius: 6,
                  width: `${demPct}%`,
                  bgcolor: isOver ? PLAN_COLORS.red : PLAN_COLORS.past,
                  opacity: 0.75,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                color: 'text.secondary',
                fontFamily: 'monospace',
                minWidth: 90,
                textAlign: 'right',
              }}
            >
              {dem.toLocaleString()}/{capV.toLocaleString()}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                minWidth: 40,
                textAlign: 'right',
                fontFamily: 'monospace',
                color: utilColor,
              }}
            >
              {util}%
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', minWidth: 55, textAlign: 'right' }}>
              {addrCount} addr
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
